import { Caption1, makeStyles, mergeClasses, Tab, TabList, tokens } from "@fluentui/react-components";
import { Board16Regular, ClipboardTaskList16Regular, Eye16Regular, EyeOff16Regular, Money16Regular, Note16Regular, People16Regular, PeopleTeam16Regular } from "@fluentui/react-icons";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDataTableStyles } from "@/components/data-table";
import { FuiButton, FuiFilterSelect, FuiSearchBox } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { lessonRows, type LessonRow } from "@/features/retrospective/retrospective-model";
import { ListToolbar } from "@/components/list-toolbar";
import { PortfolioBudgetView, PortfolioTeamView, type PeopleScope, type PlanScope, type TimeScale } from "@/features/resources/PortfolioViews";
import { distinctYears, portfolioMonths, projectCostSummary } from "@/features/resources/portfolio";
import { fmtCurrency, fmtMM } from "@/lib/format";
import { ResourcePoolDrawer } from "@/features/resources/ResourcePoolDrawer";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { formatDate } from "@/lib/date-format";
import { contentShell, shellGutter } from "@/shell/content-frame";
import { PortfolioRaidFilters, PortfolioRaidTable, usePortfolioRaid } from "@/features/raid/PortfolioRaidView";
import { useCatalogStore } from "@/store/catalog-store";

const useStyles = makeStyles({
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    padding: `0 ${shellGutter} ${tokens.spacingVerticalXXL}`,
    boxSizing: "border-box",
  },
});

export type PortfolioSection = "plan" | "people" | "teams" | "cost" | "raid" | "lessons";

/** 예전 `?view=team`(인력 표)과 `?view=budget`(플랜별 비용)은 플랜 탭으로 연다. */
function parsePortfolioSection(raw: string | null): PortfolioSection {
  if (raw === "people") return "people";
  if (raw === "teams") return "teams";
  if (raw === "cost") return "cost";
  if (raw === "raid") return "raid";
  if (raw === "lessons") return "lessons";
  return "plan";
}

export function PortfolioPage() {
  const styles = useStyles();
  const [params, setParams] = useSearchParams();
  const rawView = params.get("view");
  const section = parsePortfolioSection(rawView);
  useEffect(() => {
    if (rawView === "team" || rawView === "budget") setParams({}, { replace: true });
  }, [rawView, setParams]);
  const projects = useCatalogStore((s) => s.projects);
  const resources = useCatalogStore((s) => s.resources);
  const teams = useCatalogStore((s) => s.teams);
  const projectList = useMemo(() => Object.values(projects), [projects]);

  const [planScope, setPlanScope] = useState<PlanScope>("all");
  const [peopleScope, setPeopleScope] = useState<PeopleScope>("all");
  const [timeScale, setTimeScale] = useState<TimeScale>("month");
  const [yearFilter, setYearFilter] = useState("all");
  const [masked, setMasked] = useState(false);
  const [profileId, setProfileId] = useState<string | null>(null);
  const raid = usePortfolioRaid("");
  const [lessonQuery, setLessonQuery] = useState("");
  const lessons = useMemo(() => (section === "lessons" ? lessonRows(projectList, lessonQuery) : []), [section, projectList, lessonQuery]);
  const table = useDataTableStyles();

  const years = useMemo(() => distinctYears(portfolioMonths(projectList)), [projectList]);
  const showScale = section === "plan" || section === "people";
  const description = useMemo(() => {
    const people = new Set<string>();
    let mm = 0;
    let spend = 0;
    for (const project of projectList) {
      const summary = projectCostSummary(project, resources);
      mm += summary.plannedMM;
      spend += summary.total;
      for (const assignment of project.resourceAssignments ?? []) people.add(assignment.resourceId);
    }
    return [`${projectList.length}개 플랜`, `${people.size}명`, fmtMM(Math.round(mm * 10) / 10), `소진 ${fmtCurrency(spend, masked)}`].join(" · ");
  }, [projectList, resources, masked]);

  const selectSection = (next: PortfolioSection) => {
    if (next === section) return;
    raid.reset();
    if (next === "plan") setParams({}, { replace: true });
    else setParams({ view: next }, { replace: true });
  };

  return (
    <div className={styles.page}>
      <ListToolbar
        title="포트폴리오"
        description={description}
        titleAside={section === "raid" && raid.workspaceId === "all" ? <Caption1>모든 워크스페이스</Caption1> : undefined}
        headingAction={
          <FuiButton appearance="subtle" icon={masked ? <EyeOff16Regular /> : <Eye16Regular />} onClick={() => setMasked((on) => !on)}>
            {masked ? "단가 비공개" : "단가 공개"}
          </FuiButton>
        }
        sections={
          <TabList
            size="small"
            selectedValue={section}
            aria-label="포트폴리오"
            onTabSelect={(_, data) => {
              const next = data.value;
              if (next === "plan" || next === "people" || next === "teams" || next === "cost" || next === "raid" || next === "lessons") selectSection(next);
            }}
          >
            <Tab value="plan" icon={<Board16Regular />}>플랜</Tab>
            <Tab value="people" icon={<People16Regular />}>인력</Tab>
            <Tab value="teams" icon={<PeopleTeam16Regular />}>팀</Tab>
            <Tab value="cost" icon={<Money16Regular />}>비용</Tab>
            <Tab value="raid" icon={<ClipboardTaskList16Regular />}>RAID</Tab>
            <Tab value="lessons" icon={<Note16Regular />}>회고/교훈</Tab>
          </TabList>
        }
        search={
          section === "lessons" ? (
            <FuiSearchBox aria-label="교훈 검색" placeholder="교훈" value={lessonQuery} onChange={(_, data) => setLessonQuery(data.value)} style={{ width: 220 }} />
          ) : undefined
        }
        filters={
          section === "raid" ? (
            <PortfolioRaidFilters raid={raid} />
          ) : section === "plan" || section === "people" ? (
          <>
            {section === "plan" ? (
              <SegmentedControl
                ariaLabel="플랜 범위"
                value={planScope}
                onChange={setPlanScope}
                options={[
                  { value: "all", label: "전체" },
                  { value: "attention", label: "주의" },
                ]}
              />
            ) : null}
            {section === "people" ? (
              <SegmentedControl
                ariaLabel="인력 범위"
                value={peopleScope}
                onChange={setPeopleScope}
                options={[
                  { value: "all", label: "전체" },
                  { value: "over", label: "과부하" },
                  { value: "multi", label: "여러 플랜" },
                ]}
              />
            ) : null}
            {showScale ? (
              <FuiFilterSelect
                label="눈금"
                ariaLabel="눈금"
                value={timeScale}
                options={[
                  { value: "month", label: "월" },
                  { value: "quarter", label: "분기" },
                ]}
                onValueChange={(value) => setTimeScale(value === "quarter" ? "quarter" : "month")}
              />
            ) : null}
            {showScale && years.length > 1 ? (
              <FuiFilterSelect
                label="기간"
                ariaLabel="기간"
                value={yearFilter}
                options={[{ value: "all", label: "전체" }, ...years.map((year) => ({ value: year, label: formatDate(`${year}-01`, "year") }))]}
                onValueChange={setYearFilter}
              />
            ) : null}
          </>
          ) : undefined
        }
      />

      <div className={styles.body}>
        {section === "lessons" ? (
          <LessonTable styles={table} rows={lessons} />
        ) : section === "raid" ? (
          <PortfolioRaidTable raid={raid} />
        ) : section === "cost" ? (
          <PortfolioBudgetView projects={projectList} resources={resources} masked={masked} groupBy="costType" showKpis={false} />
        ) : (
          <PortfolioTeamView
            projects={projectList}
            resources={resources}
            teams={teams}
            masked={masked}
            groupBy={section === "people" ? "person" : section === "teams" ? "team" : "project"}
            showBudget={section === "plan"}
            showMatrix={section !== "teams"}
            peopleScope={peopleScope}
            planScope={planScope}
            timeScale={timeScale}
            yearFilter={yearFilter}
            showKpis={false}
            onOpenResource={section === "people" ? setProfileId : undefined}
          />
        )}
      </div>

      <ResourcePoolDrawer open={Boolean(profileId)} onClose={() => setProfileId(null)} initialResourceId={profileId} masked={masked} />
    </div>
  );
}

function LessonTable({ styles, rows }: { styles: ReturnType<typeof useDataTableStyles>; rows: LessonRow[] }) {
  if (!rows.length) return <GhostEmptyState title="완료된 플랜의 교훈이 없습니다" />;
  return (
    <div className={styles.frame}>
      <table className={styles.table} aria-label="회고">
        <thead>
          <tr>
            <th scope="col" className={styles.th}>플랜</th>
            <th scope="col" className={mergeClasses(styles.th, styles.num)}>SPI</th>
            <th scope="col" className={mergeClasses(styles.th, styles.num)}>CPI</th>
            <th scope="col" className={mergeClasses(styles.th, styles.num)}>종료 편차</th>
            <th scope="col" className={styles.th}>교훈</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.projectId}>
              <td className={styles.td} title={row.projectName}>
                <span className={styles.clip}>{row.projectName}</span>
              </td>
              <td className={mergeClasses(styles.td, styles.num)}>{row.spi}</td>
              <td className={mergeClasses(styles.td, styles.num)}>{row.cpi}</td>
              <td className={mergeClasses(styles.td, styles.num)}>{row.finishVariance}</td>
              <td className={styles.td} title={row.lessonText || undefined}>
                <span className={styles.clip}>{row.lessonText || "—"}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
